import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAppStoreURL, APP_STORE_URL, PLAY_STORE_URL } from '../../utils/deviceDetection';
import { FaBars, FaTimes, FaGlobe, FaApple, FaGooglePlay } from 'react-icons/fa';
import './Navbar.css';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/features', label: t('nav.features') },
    { path: '/pricing', label: t('nav.pricing') },
    { path: '/faq', label: t('nav.faq') },
    { path: '/videos', label: 'Videos' },
    { path: '/contact', label: t('nav.contact') }
  ];

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'es', name: 'Español', flag: '🇪🇸' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    document.dir = lng === 'ar' ? 'rtl' : 'ltr';
    setShowLangMenu(false);
  };

  const currentLang = languages.find(lang => lang.code === i18n.language) || languages[0];

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="container">
        <div className="nav-content">
          {/* Logo */}
          <Link to="/" className="logo" onClick={() => setIsOpen(false)}>
            <img src="/main_logo.png" alt="Land Tracker" className="logo-image" />
            <span className="logo-text gradient-text">Land Tracker</span>
          </Link>

          {/* Desktop Navigation */}
          <ul className="nav-links desktop">
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link 
                  to={link.path}
                  className={location.pathname === link.path ? 'active' : ''}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>

          {/* Right Side */}
          <div className="nav-right">
            {/* Language Switcher */}
            <div className="language-switcher">
              <button 
                className="lang-button"
                onClick={() => setShowLangMenu(!showLangMenu)}
              >
                <FaGlobe />
                <span>{currentLang.flag}</span>
              </button>
              {showLangMenu && (
                <div className="lang-menu">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={i18n.language === lang.code ? 'active' : ''}
                    >
                      <span className="flag">{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Download Button */}
            <a href={getAppStoreURL()} target="_blank" rel="noopener noreferrer" className="btn btn-primary download-btn">
              <FaApple />
              <FaGooglePlay />
              <span>{t('nav.downloadApp')}</span>
            </a>

            {/* Mobile Toggle */}
            <button 
              className="mobile-toggle"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              {isOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`mobile-menu ${isOpen ? 'open' : ''}`}>
          <ul className="nav-links mobile">
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link 
                  to={link.path}
                  className={location.pathname === link.path ? 'active' : ''}
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mobile-actions">
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              <FaApple /> App Store
            </a>
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              <FaGooglePlay /> Google Play
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
